import { useId } from 'react';
import type { TableDataType } from '@data/components/basic/Table/types';
import { cn } from '@data/utils/cn/cn';
import { formatTableData } from '@data/utils/format-table-data';
import {
  getProficiencyLevelByLabel,
  PROFICIENCY_LEVELS,
  type ProficiencyLevel,
} from '@data/utils/proficiency/proficiency';

const COLORS: Record<ProficiencyLevel, string> = {
  LEARNING: '#FE0000',
  PRACTICING: '#EF7000',
  PROFICIENT: '#D837AF',
};

/** The hatch each level is drawn with, so levels differ beyond colour. */
function Hatch({ level, color }: { level: ProficiencyLevel; color: string }) {
  if (level === 'PRACTICING') {
    return (
      <>
        <rect x="0" y="0" width="1.4" height="3.5" fill={color} />
        <rect x="0" y="0" width="3.5" height="1.4" fill={color} />
      </>
    );
  }
  return <rect x="0" y="0" width="1.6" height="3.2" fill={color} />;
}

/** The patterned square that stands for a proficiency level. */
export function ProficiencySwatch({
  level,
  className,
}: {
  level: ProficiencyLevel;
  className?: string;
}) {
  const patternId = `proficiency-${useId()}`;
  const color = COLORS[level];
  const isGrid = level === 'PRACTICING';
  let rotation = 0;
  if (level === 'LEARNING') rotation = 45;
  if (level === 'PROFICIENT') rotation = -45;

  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden="true"
      className={cn('size-3 shrink-0', className)}
    >
      <defs>
        <pattern
          id={patternId}
          width={isGrid ? 3.5 : 3.2}
          height={isGrid ? 3.5 : 3.2}
          patternUnits="userSpaceOnUse"
          patternTransform={`rotate(${rotation})`}
        >
          <Hatch level={level} color={color} />
        </pattern>
      </defs>
      <rect width="12" height="12" rx="2" fill={`url(#${patternId})`} />
    </svg>
  );
}

/** The key above a proficiency table, naming each level and its range. */
export function ProficiencyLegend() {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-neutral-1300">
        Proficiency Level
      </span>
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {PROFICIENCY_LEVELS.map(({ level, label, range }) => (
          <li
            key={level}
            className="flex items-center gap-1.5 whitespace-nowrap text-sm text-neutral-1300"
          >
            <ProficiencySwatch level={level} />
            {label} {range}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A "Most students" cell: the level's swatch, named for screen readers, or
 * the table's own text for a value that is not a level — centred either way.
 */
export function renderMostStudentsCell(value: TableDataType) {
  const level = getProficiencyLevelByLabel(value);
  if (!level) {
    return (
      <span className="flex justify-center">
        {formatTableData(value, 'string')}
      </span>
    );
  }
  return (
    <span className="flex justify-center">
      <ProficiencySwatch level={level} />
      <span className="sr-only">{String(value)}</span>
    </span>
  );
}

/** A level cell: the level's swatch beside its name. */
export function renderLevelCell(value: unknown) {
  const level = getProficiencyLevelByLabel(value);
  if (!level) return undefined;
  return (
    <span className="flex items-center gap-1.5">
      <ProficiencySwatch level={level} />
      {String(value)}
    </span>
  );
}
